import { useCallback, useEffect, useState } from "react";
import { faPlus, faPencil, faTrash } from "@fortawesome/free-solid-svg-icons";
import Icon from "../../common/elements/Icon";
import Button from "../../common/elements/Button";
import HelpTooltip from "./HelpTooltip";
import { HELP } from "../utils/thorHelp";
import StateHandler from "../../common/elements/StateHandler";
import SmallLoadingSpinner from "../../common/elements/SmallLoadingSpinner";
import InfoCard from "../../common/elements/InfoCard";
import useFetchWithMsal from "../../../useFetchWithMsal";
import { thor_api_urls } from "../../common/Constants";
import { buildDataState, buildErrorState, buildLoadingState } from "../../../utils/StateUtils";
import { useThor } from "../context/ThorContext";
import { serverErrMsg } from "../utils/validators";
import ConfirmDialog from "./ConfirmDialog";

const VARIABLE_TYPES = ['boolean', 'string', 'double', 'integer', 'json'];

const TYPE_COLORS = {
    boolean: 'is-warning is-light',
    string:  'is-info is-light',
    double:  'is-success is-light',
    integer: 'is-link is-light',
    json:    'is-dark',
};

// ── Variable modal (add / edit) ────────────────────────────────────────────

const VariableModal = ({ isActive, editVariable, isSubmitting, onClose, onSubmit }) => {
    const [form, setForm] = useState({ key: '', type: 'string', defaultValue: '' });

    useEffect(() => {
        if (isActive) {
            setForm(editVariable
                ? { key: editVariable.key, type: editVariable.type, defaultValue: editVariable.defaultValue }
                : { key: '', type: 'string', defaultValue: '' }
            );
        }
    }, [isActive, editVariable]);

    const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

    const isJsonInvalid = form.type === 'json' && form.defaultValue.trim() !== '' && (() => {
        try { JSON.parse(form.defaultValue); return false; } catch { return true; }
    })();

    return (
        <div className={`modal ${isActive ? 'is-active' : ''}`}>
            <div className="modal-background" onClick={onClose} />
            <div className="modal-card">
                <header className="modal-card-head">
                    <p className="modal-card-title">{editVariable ? 'Edit Variable' : 'Add Variable'}</p>
                    <button className="delete" onClick={onClose} />
                </header>
                <section className="modal-card-body">
                    <div className="field">
                        <label className="label is-small">Key <span className="has-text-danger">*</span></label>
                        <div className="control">
                            <input className="input is-small" type="text" value={form.key}
                                   disabled={!!editVariable}
                                   onChange={e => set('key', e.target.value)}
                                   placeholder="e.g. show_banner" />
                        </div>
                        {!!editVariable && <p className="help">Key cannot be changed after creation.</p>}
                    </div>
                    <div className="field">
                        <label className="label is-small">Type <span className="has-text-danger">*</span></label>
                        <div className="control">
                            <div className="select is-small is-fullwidth">
                                <select value={form.type} disabled={!!editVariable}
                                        onChange={e => set('type', e.target.value)}>
                                    {VARIABLE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                                </select>
                            </div>
                        </div>
                    </div>
                    <div className="field">
                        <label className="label is-small">Default Value <span className="has-text-danger">*</span></label>
                        <div className="control">
                            <input className={`input is-small ${isJsonInvalid ? 'is-danger' : ''}`}
                                   type="text" value={form.defaultValue}
                                   onChange={e => set('defaultValue', e.target.value)}
                                   placeholder={form.type === 'json' ? '{"key": "value"}' : 'Value used when no bucket override is set'} />
                        </div>
                        {isJsonInvalid && <p className="help is-danger">Invalid JSON — must be valid JSON (e.g. {`{"key": "value"}`}).</p>}
                    </div>
                </section>
                <footer className="modal-card-foot">
                    <Button className="is-link mr-2" disabled={isSubmitting || !form.key.trim() || !form.defaultValue.trim() || isJsonInvalid}
                            onClick={() => onSubmit(form)}>
                        {isSubmitting ? 'Saving…' : (editVariable ? 'Save' : 'Add Variable')}
                    </Button>
                    <Button onClick={onClose}>Cancel</Button>
                </footer>
            </div>
        </div>
    );
};

// ── Cell value edit modal ──────────────────────────────────────────────────

const validateCellValue = (type, val) => {
    if (val === '') return null;
    switch (type) {
        case 'boolean':
            return val === 'true' || val === 'false' ? null : 'Must be "true" or "false".';
        case 'integer':
            return /^-?\d+$/.test(val) ? null : 'Must be a whole number (no decimals).';
        case 'double':
            return isNaN(Number(val)) || val.trim() === '' ? 'Must be a number.' : null;
        case 'json':
            try { JSON.parse(val); return null; } catch { return 'Invalid JSON.'; }
        default:
            return null;
    }
};

const CellEditModal = ({ isActive, variable, bucket, currentValue, isSubmitting, onClose, onSave, onClear }) => {
    const [value, setValue] = useState('');

    useEffect(() => {
        if (isActive) setValue(currentValue ?? '');
    }, [isActive, currentValue]);

    if (!variable || !bucket) return null;

    const cellError = validateCellValue(variable.type, value);

    return (
        <div className={`modal ${isActive ? 'is-active' : ''}`}>
            <div className="modal-background" onClick={onClose} />
            <div className="modal-card">
                <header className="modal-card-head">
                    <p className="modal-card-title">
                        {variable.key} — {bucket.key}
                    </p>
                    <button className="delete" onClick={onClose} />
                </header>
                <section className="modal-card-body">
                    <p className="has-text-grey is-size-7 mb-3">
                        Type: <strong>{variable.type}</strong> · Default: <code>{variable.defaultValue}</code>
                    </p>
                    <div className="field">
                        <label className="label is-small">Value for this bucket</label>
                        <div className="control">
                            <input className={`input is-small ${cellError ? 'is-danger' : ''}`}
                                   type="text" value={value}
                                   onChange={e => setValue(e.target.value)}
                                   placeholder={`Default: ${variable.defaultValue}`} />
                        </div>
                        {cellError && <p className="help is-danger">{cellError}</p>}
                    </div>
                </section>
                <footer className="modal-card-foot" style={{ gap: '0.5rem' }}>
                    <Button className="is-link mr-2" disabled={isSubmitting || value === '' || !!cellError}
                            onClick={() => onSave(value)}>
                        {isSubmitting ? 'Saving…' : 'Save'}
                    </Button>
                    {currentValue != null && (
                        <Button className="is-danger is-light mr-2" disabled={isSubmitting}
                                onClick={onClear}>
                            Clear (use default)
                        </Button>
                    )}
                    <Button onClick={onClose}>Cancel</Button>
                </footer>
            </div>
        </div>
    );
};

// ── Main component ─────────────────────────────────────────────────────────

const VariablesTab = ({ experimentKey }) => {
    const { execute } = useFetchWithMsal();
    const { editMode, addToast } = useThor();

    const [variablesState, setVariablesState] = useState(buildLoadingState([]));
    const [bucketsState, setBucketsState] = useState(buildLoadingState([]));
    // Map: bucketId -> { variableId -> value }
    const [bucketValues, setBucketValues] = useState({});
    const [loadingValues, setLoadingValues] = useState(false);

    const [varModalOpen, setVarModalOpen] = useState(false);
    const [editVariable, setEditVariable] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [cellModal, setCellModal] = useState(null); // { variable, bucket, currentValue }
    const [isCellSaving, setIsCellSaving] = useState(false);
    const [deletingId, setDeletingId] = useState(null);
    const [confirmDelete, setConfirmDelete] = useState(null);

    const fetchVariables = useCallback(() => {
        setVariablesState(buildLoadingState([]));
        execute('GET', thor_api_urls.experimentVariables(experimentKey))
            .then(data => setVariablesState(buildDataState(data?.variables ?? [])))
            .catch(err => {
                console.error("Failed to fetch variables:", err);
                setVariablesState(buildErrorState(err, []));
            });
    }, [execute, experimentKey]);

    const fetchBuckets = useCallback(() => {
        setBucketsState(buildLoadingState([]));
        execute('GET', thor_api_urls.experimentBuckets(experimentKey))
            .then(data => setBucketsState(buildDataState(data?.buckets ?? data ?? [])))
            .catch(err => {
                console.error("Failed to fetch buckets:", err);
                setBucketsState(buildErrorState(err, []));
            });
    }, [execute, experimentKey]);

    const fetchAllBucketValues = useCallback((buckets) => {
        if (!buckets?.length) return;
        setLoadingValues(true);
        Promise.all(
            buckets.map(b =>
                execute('GET', thor_api_urls.experimentVariableValuesByBucket(experimentKey, b.id))
                    .then(data => ({ bucketId: b.id, values: data?.variableValues ?? [] }))
                    .catch(() => ({ bucketId: b.id, values: [] }))
            )
        ).then(results => {
            const map = {};
            results.forEach(({ bucketId, values }) => {
                map[bucketId] = {};
                values.forEach(v => { map[bucketId][v.variableId] = v.value; });
            });
            setBucketValues(map);
        }).finally(() => setLoadingValues(false));
    }, [execute, experimentKey]);

    useEffect(() => { fetchVariables(); fetchBuckets(); }, [fetchVariables, fetchBuckets]);

    const bucketIds = (bucketsState.data ?? []).map(b => b.id).join(',');
    useEffect(() => {
        if (bucketsState.data?.length) fetchAllBucketValues(bucketsState.data);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bucketIds, fetchAllBucketValues]);

    const handleVarSubmit = (form) => {
        setIsSubmitting(true);
        const req = editVariable
            ? execute('PUT', thor_api_urls.experimentVariable(experimentKey, editVariable.id), form)
            : execute('POST', thor_api_urls.experimentVariables(experimentKey), form);
        req
            .then(() => {
                setVarModalOpen(false);
                fetchVariables();
                addToast(editVariable ? 'Variable updated.' : 'Variable added.', 'success');
            })
            .catch(err => addToast(serverErrMsg(err, 'Failed to save variable.'), 'danger'))
            .finally(() => setIsSubmitting(false));
    };

    const handleVarDelete = (v) => {
        setDeletingId(v.variableId ?? v.id);
        execute('DELETE', thor_api_urls.experimentVariable(experimentKey, v.id))
            .then(() => { fetchVariables(); addToast('Variable deleted.', 'success'); })
            .catch(err => addToast(serverErrMsg(err, 'Failed to delete variable.'), 'danger'))
            .finally(() => setDeletingId(null));
    };

    const handleCellSave = (value) => {
        const { variable, bucket } = cellModal;
        setIsCellSaving(true);
        execute('POST', thor_api_urls.experimentVariableValues(experimentKey), {
            bucketId: bucket.id,
            variableId: variable.id,
            value,
        })
            .then(() => {
                setBucketValues(prev => ({
                    ...prev,
                    [bucket.id]: { ...(prev[bucket.id] ?? {}), [variable.id]: value },
                }));
                setCellModal(null);
                addToast('Value saved.', 'success');
            })
            .catch(err => addToast(serverErrMsg(err, 'Failed to save value.'), 'danger'))
            .finally(() => setIsCellSaving(false));
    };

    const handleCellClear = () => {
        const { variable, bucket } = cellModal;
        setIsCellSaving(true);
        // DELETE with body — non-standard, send as JSON body
        execute('DELETE', thor_api_urls.experimentVariableValues(experimentKey), {
            bucketId: bucket.id,
            variableId: variable.id,
        })
            .then(() => {
                setBucketValues(prev => {
                    const next = { ...prev, [bucket.id]: { ...(prev[bucket.id] ?? {}) } };
                    delete next[bucket.id][variable.id];
                    return next;
                });
                setCellModal(null);
                addToast('Value cleared — bucket will use default.', 'success');
            })
            .catch(err => addToast(serverErrMsg(err, 'Failed to clear value.'), 'danger'))
            .finally(() => setIsCellSaving(false));
    };

    const variables = variablesState.data ?? [];
    const buckets = bucketsState.data ?? [];
    const loading = variablesState.loading || bucketsState.loading;
    const hasError = variablesState.error || bucketsState.error;

    return (
        <>
            {/* ── Variables ── */}
            <div className="level mb-3">
                <div className="level-left">
                    <p className="subtitle is-5 mb-0">
                        Variables<HelpTooltip text={HELP.variables.general} />
                    </p>
                </div>
                <div className="level-right">
                    <Button className="is-link is-small" disabled={!editMode}
                            onClick={() => { setEditVariable(null); setVarModalOpen(true); }}>
                        <Icon icon={faPlus} /><span>Add Variable</span>
                    </Button>
                </div>
            </div>

            <p className="has-text-grey is-size-7 mb-3">
                Variables are named values (string, number, boolean, JSON) attached to each bucket. Instead of hard-coding behavior per variant, read the variable value at runtime. <strong>Do not change variable values after the experiment goes live</strong> — it can invalidate the analysis.
            </p>

            <StateHandler showLoader={loading} showError={hasError} loadingScreen={<SmallLoadingSpinner />}>
                {variables.length === 0 ? (
                    <InfoCard title="No variables defined for this experiment." />
                ) : (
                    <table className="table is-fullwidth is-striped is-hoverable mb-5">
                        <thead>
                            <tr>
                                <th>Key</th>
                                <th>Type</th>
                                <th>Default Value</th>
                                <th className="has-text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {variables.map(v => (
                                <tr key={v.id}>
                                    <td className="has-text-weight-semibold is-family-monospace" style={{ fontSize: '0.85rem' }}>{v.key}</td>
                                    <td>
                                        <span className={`tag is-small ${TYPE_COLORS[v.type] ?? 'is-light'}`}>{v.type}</span>
                                    </td>
                                    <td className="has-text-grey">{v.defaultValue}</td>
                                    <td className="has-text-right">
                                        <div style={{ display: 'inline-flex', gap: '0.35rem' }}>
                                            <button type="button"
                                                className="button is-small is-ghost"
                                                aria-label="Edit variable"
                                                style={{ padding: '0 0.4rem' }}
                                                disabled={!editMode || deletingId != null}
                                                onClick={editMode ? () => { setEditVariable(v); setVarModalOpen(true); } : undefined}>
                                                <Icon icon={faPencil} />
                                            </button>
                                            <button type="button"
                                                className="button is-small is-ghost has-text-danger"
                                                aria-label="Delete variable"
                                                style={{ padding: '0 0.4rem' }}
                                                disabled={!editMode || deletingId === (v.variableId ?? v.id)}
                                                onClick={editMode ? () => setConfirmDelete(v) : undefined}>
                                                <Icon icon={faTrash} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}

                {/* ── Bucket values matrix ── */}
                {variables.length > 0 && buckets.length > 0 && (
                    <>
                        <p className="subtitle is-6 mb-3">
                            Bucket Values<HelpTooltip text={HELP.variables.bucketValues} />
                            {loadingValues && <span className="ml-2"><SmallLoadingSpinner /></span>}
                        </p>
                        {!editMode && (
                            <p className="help mb-2">Enable Edit mode to set per-bucket variable values.</p>
                        )}
                        <p className="has-text-grey is-size-7 mb-3">
                            {editMode
                                ? 'Click a cell to set a per-bucket override. Cells showing the default value have no override set.'
                                : 'Enable Edit mode to set per-bucket values.'}
                        </p>
                        <div style={{ overflowX: 'auto' }}>
                            <table className={`table is-fullwidth is-bordered ${editMode ? 'is-hoverable' : ''}`}>
                                <thead>
                                    <tr>
                                        <th style={{ minWidth: '140px' }}>Variable</th>
                                        {buckets.map(b => (
                                            <th key={b.id} className="has-text-centered" style={{ minWidth: '120px' }}>
                                                <div>{b.description || b.key}</div>
                                                <div className="has-text-grey is-family-monospace" style={{ fontSize: '0.75rem', fontWeight: 'normal' }}>{b.key}</div>
                                                {b.control && <span className="tag is-success is-light is-small mt-1">control</span>}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {variables.map(v => (
                                        <tr key={v.id}>
                                            <td>
                                                <div className="is-family-monospace is-size-7 has-text-weight-semibold">{v.key}</div>
                                                <span className={`tag is-small ${TYPE_COLORS[v.type] ?? 'is-light'}`}>{v.type}</span>
                                            </td>
                                            {buckets.map(b => {
                                                const override = bucketValues[b.id]?.[v.id];
                                                const hasOverride = override != null;
                                                return (
                                                    <td key={b.id} className="has-text-centered"
                                                        style={{ cursor: editMode ? 'pointer' : 'default', background: hasOverride ? '#f0f4ff' : undefined }}
                                                        onClick={editMode ? () => setCellModal({ variable: v, bucket: b, currentValue: override ?? null }) : undefined}>
                                                        {hasOverride
                                                            ? <span className="tag is-link is-light is-small">{override}</span>
                                                            : <span className="has-text-grey is-size-7">{v.defaultValue}</span>}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </StateHandler>

            <VariableModal isActive={varModalOpen} editVariable={editVariable}
                           isSubmitting={isSubmitting}
                           onClose={() => setVarModalOpen(false)}
                           onSubmit={handleVarSubmit} />

            <CellEditModal isActive={!!cellModal} variable={cellModal?.variable}
                           bucket={cellModal?.bucket} currentValue={cellModal?.currentValue}
                           isSubmitting={isCellSaving}
                           onClose={() => setCellModal(null)}
                           onSave={handleCellSave}
                           onClear={handleCellClear} />

            <ConfirmDialog
                isActive={confirmDelete !== null}
                title="Delete Variable"
                message={`Delete variable "${confirmDelete?.key}"? This cannot be undone.`}
                confirmLabel="Delete"
                confirmClass="is-danger"
                onConfirm={() => { handleVarDelete(confirmDelete); setConfirmDelete(null); }}
                onCancel={() => setConfirmDelete(null)}
            />
        </>
    );
};

export default VariablesTab;
